/**
 * <park-cta> — closing call-to-action band (epic #138, issue #188; see
 * docs/design-system-rebuild-plan.md). Lifts the `.home-cta` closing section
 * from HomePage.astro into the isomorphic park-* contract.
 *
 * Single source of truth = the pure `renderParkCta()` below, consumed by BOTH:
 *   - the live Astro site, via the SSR wrapper `components/ds/ParkCta.astro`,
 *     which calls it server-side and emits the markup inside <park-cta data-ssr>
 *     so the band is in the initial HTML (SEO / no-JS), and
 *   - the Claude-Design preview, where the custom element renders client-side.
 * Because both call the SAME function, server and client markup are identical.
 *
 * The CTA control stays a SLOT (default slot) — the page keeps its own
 * `locUrl()` park-button anchor; it is NOT reconstructed here.
 *
 * Emits the exact live class names (`.home-cta`, with `.park-wrap` inside); the
 * scoped CSS lives in `./css/park-cta.css` (`.park-wrap` is shared, in park.css).
 *
 * Attributes:
 *   - heading="…"  the <h2> text.
 *   - body="…"     the <p> text.
 * Slotted content = the CTA control (a park-button / anchor).
 *
 * @dsSpec design-system#FR-3 — the recurring closing call-to-action band.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-cta";

export interface ParkCtaProps {
	heading?: string;
	body?: string;
}

/**
 * Pure render: (props, CTA HTML) → inner HTML string. No DOM access, so it is
 * safe to call server-side (the .astro wrapper) and client-side (the element).
 * `ctaHtml` is trusted author markup (already escaped/rendered by the caller).
 */
export function renderParkCta(props: ParkCtaProps, ctaHtml: string): string {
	const heading = props.heading ? html`<h2>${esc(props.heading)}</h2>` : "";
	const body = props.body ? html`<p>${esc(props.body)}</p>` : "";
	return html`<section class="home-cta"><div class="park-wrap">${heading}${body}${ctaHtml}</div></section>`;
}

class ParkCta extends ParkElement {
	static get observedAttributes(): string[] {
		return ["heading", "body"];
	}

	protected render(): string {
		return renderParkCta(
			{
				heading: this.attr("heading"),
				body: this.attr("body"),
			},
			this.slotHtml,
		);
	}
}

defineElement(TAG, ParkCta);

export { ParkCta };
